import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import AssistantQueryService from '@services/AssistantQueryService';

import type { AiEntity } from './types';
import { useAiInspectorStyles } from './inspectorStyles';

interface IExchange {
  question: string;
  answer: string | null;
  sources: string[];
  latencyMs: number | null;
  error: string | null;
  busy: boolean;
}

interface IAssistantTesterProps {
  entity: AiEntity;
  brandingId: string | undefined;
  mapName: string | undefined;
}

interface ModelResponse {
  text?: unknown;
  sources?: unknown;
  used?: unknown;
}

// The backend may return either {sources:[{title,score}]} or {used:[...]} —
// pull whatever it sent into a flat list of `title · score` strings.
const extractSources = (response: ModelResponse): string[] => {
  const raw = Array.isArray(response.sources)
    ? response.sources
    : Array.isArray(response.used)
    ? response.used
    : [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry;
      }
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const obj = entry as { title?: unknown; score?: unknown };
      const title = typeof obj.title === 'string' ? obj.title : null;
      if (!title) {
        return null;
      }
      const score =
        typeof obj.score === 'number' ? ` · ${obj.score.toFixed(2)}` : '';
      return `${title}${score}`;
    })
    .filter((entry): entry is string => Boolean(entry));
};

const AssistantTester = ({
  entity,
  brandingId,
  mapName,
}: IAssistantTesterProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();
  const { currentEventId } = useMytaverseEvent();

  const [exchanges, setExchanges] = useState<IExchange[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const send = useCallback(async () => {
    const trimmed = question.trim();
    if (!trimmed || busy) {
      return;
    }
    setBusy(true);
    const placeholderIndex = exchanges.length;
    setExchanges((prev) => [
      ...prev,
      {
        question: trimmed,
        answer: null,
        sources: [],
        latencyMs: null,
        error: null,
        busy: true,
      },
    ]);
    setQuestion('');

    const start = performance.now();
    try {
      // Scope the question to this entity's region/branding/map so the
      // backend runs RAG against just this area's knowledge (isNarrowContext).
      const response = (await AssistantQueryService.queryAssistant({
        text: trimmed,
        tag: entity.primaryTrigger,
        eventId: currentEventId ?? 'debug',
        brandingId,
        mapName,
        isNarrowContext: true,
      })) as ModelResponse;
      const latencyMs = Math.round(performance.now() - start);
      const answer =
        typeof response.text === 'string'
          ? response.text
          : translate('editorMode.aiTesterNoText');
      setExchanges((prev) => {
        const next = [...prev];
        next[placeholderIndex] = {
          ...next[placeholderIndex],
          answer,
          sources: extractSources(response),
          latencyMs,
          error: null,
          busy: false,
        };
        return next;
      });
    } catch (queryError) {
      const latencyMs = Math.round(performance.now() - start);
      console.error(
        `Assistant tester query failed: ${(queryError as Error).message}`,
        { trigger: entity.primaryTrigger, brandingId, mapName },
      );
      setExchanges((prev) => {
        const next = [...prev];
        next[placeholderIndex] = {
          ...next[placeholderIndex],
          answer: null,
          sources: [],
          latencyMs,
          error: (queryError as Error).message,
          busy: false,
        };
        return next;
      });
    } finally {
      setBusy(false);
      // Give DOM a tick to finish render, then focus the input again.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [
    question,
    busy,
    exchanges.length,
    entity.primaryTrigger,
    brandingId,
    currentEventId,
    mapName,
    translate,
  ]);

  return (
    <Box>
      {exchanges.map((exchange, index) => (
        <Box key={`exchange-${index}`}>
          <Box sx={classes.userBubble}>
            <Box component="span">{exchange.question}</Box>
          </Box>
          {exchange.busy ? (
            <Box sx={[classes.assistantBubble, classes.thinkingDots]}>
              <i />
              <i />
              <i />
            </Box>
          ) : (
            <Box sx={classes.assistantBubble}>
              {exchange.error ? (
                <Typography
                  component="span"
                  sx={{ color: '#f6a8a8', fontSize: '12px' }}
                >
                  {translate('editorMode.aiTesterError', {
                    message: exchange.error,
                  })}
                </Typography>
              ) : (
                <>
                  <Typography
                    component="span"
                    sx={{ fontSize: '12px', color: '#dadce4' }}
                  >
                    {exchange.answer ?? ''}
                  </Typography>
                  {exchange.sources.length > 0 ? (
                    <Box sx={classes.bubbleSources}>
                      {exchange.sources.map((source) => (
                        <Box
                          key={source}
                          component="span"
                          sx={[classes.metaChip, { padding: '2.5px 8px' }]}
                        >
                          <TextSnippetOutlinedIcon />
                          {source}
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                  <Typography sx={classes.bubbleMeta}>
                    {exchange.latencyMs !== null
                      ? `${exchange.latencyMs} ms · POST /assistant/query/model`
                      : 'POST /assistant/query/model'}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      ))}

      <Box
        sx={[
          classes.testerInputRow,
          { marginTop: exchanges.length ? '10px' : '2px' },
        ]}
      >
        <Box
          component="input"
          ref={inputRef}
          sx={classes.testerInput}
          placeholder={translate('editorMode.aiTesterPlaceholder', 'Ask as a visitor…')}
          value={question}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setQuestion(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void send();
            } else if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          disabled={busy}
        />
        <Tooltip title={translate('editorMode.aiTesterSend', 'Send test question')}>
          <IconButton
            sx={classes.sendButton}
            onClick={() => void send()}
            disabled={busy || !question.trim()}
            aria-label={translate('editorMode.aiTesterSend', 'Send test question')}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default AssistantTester;
